import {
  Component,
  inject,
  computed,
  signal,
  input,
  ChangeDetectionStrategy,
  output,
  OnInit,
} from '@angular/core';
import {
  AlertService,
  CardComponent,
  CardContentComponent, LoadingComponent,
} from '@gains-com/ui-angular';
import {
  CARD_PURCHASES_DISABLED_MESSAGE,
  coinflowErrorMessage,
  isCardPurchasesDisabledError,
  isCoinflowNameVerificationDecline,
  isModeRestrictedError,
  isPurchaseLimitReachedError,
  PurchaseSelection,
  PurchasePaymentMethod,
} from '../../common';
import { buildWalletPayProps } from '../../wallet-pay-props.js';
import { coinflowCustomerInfo } from '../../../../core/coinflow-checkout.service.js';
import {
  CurrencyId,
  decimal,
  PaymentEntityTypeId,
} from '@gains-com/shared';
import { PurchaseDialogHeaderComponent } from '../purchase-dialog-header/purchase-dialog-header.component.js';
import { PurchaseReceiptComponent } from '../purchase-receipt/purchase-receipt.component.js';
import { BillingNameWarningComponent } from '../billing-name-warning/billing-name-warning.component.js';
import { NameMismatchNoticeComponent } from '../name-mismatch-notice/name-mismatch-notice.component.js';
import { environment } from '../../../../../environments/environment.js';
import { AuthService } from '../../../../core/auth.service.js';
import {
  CoinflowApplePayButtonComponent,
  CoinflowGooglePayButtonComponent,
} from '@coinflowlabs/angular';
import { ActivePurchaseViewService } from '../../../../core/view-states/active-purchase-view.service.js';
import { LottieAnimationComponent } from '../../../lottie-animation/lottie-animation.component.js';
import { AnalyticsTrackingService } from '../../../../core/analytics-tracking.service.js';
import { CoinflowService } from '../../../../core/coinflow.js';
import { LOGGER_FACTORY } from '../../../../core/logger/logger-factory.provider.js';
import { UserViewService } from '../../../../core/view-states/user-view.service.js';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-purchase-payment-method',
  templateUrl: './purchase-payment-method.component.html',
  styleUrls: ['../styles.scss', './purchase-payment-method.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardContentComponent,
    CardComponent,
    PurchaseDialogHeaderComponent,
    PurchaseReceiptComponent,
    BillingNameWarningComponent,
    NameMismatchNoticeComponent,
    LottieAnimationComponent,
    CoinflowApplePayButtonComponent,
    CoinflowGooglePayButtonComponent,
    LoadingComponent,
  ],
})
export class PurchasePaymentMethodComponent implements OnInit {
  private readonly _activePurchaseViewService = inject(ActivePurchaseViewService);
  private readonly _auth = inject(AuthService);
  private readonly _analyticsTracking = inject(AnalyticsTrackingService);
  private readonly _coinflow = inject(CoinflowService);
  private readonly _userView = inject(UserViewService);
  private readonly _alert = inject(AlertService);
  private readonly _logger = inject(LOGGER_FACTORY).create('PurchasePaymentMethod');

  amount = computed(() => this.package()?.inputAmount?.toNumber() ?? 0);
  user = toSignal(this._userView.state$);
  fullName = computed(() =>
    `${this.user()?.first_name ?? ''} ${this.user()?.last_name ?? ''}`.trim(),
  );

  package = input.required<PurchaseSelection>();
  paymentMethod = input.required<PurchasePaymentMethod>();

  coinflowToken = signal<string | null>(null);
  mobilePayTxLoading = signal(false);
  nameMismatchDeclined = signal(false);
  mobilePayOptions = signal<{
    method: 'apple_pay' | 'google_pay';
    height: number;
  } | null>(null);

  applePayPurchaseProps = computed(() => this._walletPayProps('apple_pay'));

  googlePayPurchaseProps = computed(() => this._walletPayProps('google_pay'));

  // The GAMING wallet composition: static environment merchant, client-computed
  // cents, client-authored chargeback block, USDC-on-Solana settlement. Merch
  // composes its own from the server's charge block — the two must never share
  // composition.
  private _walletPayProps(method: 'apple_pay' | 'google_pay') {
    return buildWalletPayProps({
      charge: {
        merchantId: environment.coinflow.merchantId,
        sessionKey: this.coinflowToken(),
        subtotalCents: decimal(this.amount()).times(100).toNumber(),
        webhookInfo: this._analyticsTracking.getParams() ?? {},
        chargebackProtectionData: [
          {
            productName: 'Sweeps Coins',
            productType: 'gameOfSkill',
            quantity: this.amount(),
            rawProductData: {
              currency: 'sweeps_coins',
              amount: this.amount(),
            },
          },
        ],
        customerInfo: coinflowCustomerInfo(this.user()),
      },
      deviceId: this._coinflow.deviceId,
      email: this._auth.user?.email,
      handlers: {
        onPaymentRegistered: (paymentId) => {
          this.mobilePayTxLoading.set(true);

          this._activePurchaseViewService.setActivePurchasePaymentEntity([
            {
              payment_entity_id: paymentId,
              payment_entity_type_id: PaymentEntityTypeId.CoinflowPayment,
              currency_id: CurrencyId.USD,
            },
          ]);
        },
        onDecline: (message) => this._handleWalletDecline(method, message),
        onSheetHeightChange: (open, height) => this.mobilePayOptions.set(open ? { height, method } : null),
      },
      settlement: {
        settlementType: 'USDC',
        blockchain: 'solana',
      },
    }) as any;
  }

  readonly back = output<void>();
  readonly close = output<'contact-support' | void>();
  readonly purchaseLimitReached = output<void>();

  private _handleWalletDecline(method: 'apple_pay' | 'google_pay', message: string) {
    const err = { error: { message } };
    const errorMessage = coinflowErrorMessage(err);

    this.nameMismatchDeclined.set(isCoinflowNameVerificationDecline(err));
    this._alert.notifyDanger('Purchase Failed', errorMessage);

    this._logger.warn('Wallet purchase declined', { method, message });
  }

  async ngOnInit() {
    try {
      this.coinflowToken.set(await this._coinflow.createSessionToken('purchase'));
    } catch (err: any) {
      if (isPurchaseLimitReachedError(err)) {
        this.purchaseLimitReached.emit();
        return;
      }
      if (isModeRestrictedError(err)) {
        return;
      }
      if (isCardPurchasesDisabledError(err)) {
        this._alert.notifyDanger('Purchase Unavailable', CARD_PURCHASES_DISABLED_MESSAGE);
        return;
      }

      this._logger.error('Failed to create Coinflow session token', {
        stack: err,
      });
    }
  }
}
